import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { PDFDocument } from "pdf-lib";
import { format } from "date-fns";
import Intestazione from "../../assets/intestazione-Giuvi-3.png";

// Helper per formattare date
function formatSafeDate(dateValue) {
  if (!dateValue) return "N/A";
  let dateObj = null;

  if (typeof dateValue === "object" && typeof dateValue.toDate === "function") {
    dateObj = dateValue.toDate();
  } else {
    dateObj = new Date(dateValue);
  }

  if (dateObj && !isNaN(dateObj.getTime())) {
    return format(dateObj, "dd-MM-yyyy");
  }
  return "N/A";
}

function parseNumberLoose(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  let raw = String(value).trim();
  if (!raw) return 0;

  raw = raw.replace(/€/g, "").replace(/\s+/g, "");
  const hasDot = raw.includes(".");
  const hasComma = raw.includes(",");

  if (hasDot && hasComma) {
    const lastDot = raw.lastIndexOf(".");
    const lastComma = raw.lastIndexOf(",");
    raw =
      lastComma > lastDot
        ? raw.replace(/\./g, "").replace(/,/g, ".")
        : raw.replace(/,/g, "");
  } else if (hasComma) {
    raw = raw.replace(/,/g, ".");
  }

  raw = raw.replace(/[^0-9.-]/g, "");
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getCallFeeTotal(callFee) {
  if (!callFee || !callFee.applicato) return 0;

  const valore = parseNumberLoose(callFee.valore);
  if (valore > 0) return valore;

  const qta = parseNumberLoose(callFee.qta ?? callFee.quantita ?? 1);
  const prezzoUnitario = parseNumberLoose(
    callFee.prezzo_unitario ??
      callFee.prezzoUnitario ??
      callFee.diritto_chiamata_prezzo_unitario ??
      callFee.diritto_chiamata,
  );

  return qta * prezzoUnitario;
}

function aggregateLabor(labor = []) {
  const grouped = new Map();

  labor.forEach((item) => {
    const tipo = String(item?.nome_tecnico || "").trim() || "Manodopera";
    const tariffa = parseNumberLoose(item?.tariffa_oraria);
    const ore = parseNumberLoose(item?.totale_ore);

    if (ore <= 0) return;

    const key = `${tipo}__${tariffa}`;
    const current = grouped.get(key);

    if (current) {
      current.totale_ore += ore;
      return;
    }

    grouped.set(key, {
      nome_tecnico: tipo,
      tariffa_oraria: tariffa,
      totale_ore: ore,
    });
  });

  return Array.from(grouped.values()).sort((a, b) => {
    const tipoCmp = String(a.nome_tecnico).localeCompare(
      String(b.nome_tecnico),
      "it",
      { sensitivity: "base" },
    );
    if (tipoCmp !== 0) return tipoCmp;

    return a.tariffa_oraria - b.tariffa_oraria;
  });
}

// Helper per caricare immagini (Intestazione)
async function loadImageAsDataUrl(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const maxWidth = 800;
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      // Sfondo bianco
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, width, height);

      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// Helper per caricare immagini allegati
async function loadAttachmentAsDataUrl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = src;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = (error) => resolve(null);
  });
}

export async function downloadConsuntivoPdf({
  consuntivoData,
  documents,
  selectedIds,
  onReady,
}) {
  // 1. Prepare Data & Totals
  const monthsIT = [
    "Gennaio",
    "Febbraio",
    "Marzo",
    "Aprile",
    "Maggio",
    "Giugno",
    "Luglio",
    "Agosto",
    "Settembre",
    "Ottobre",
    "Novembre",
    "Dicembre",
  ];
  const datesDoc = Array.isArray(documents)
    ? documents
        .map((d) => {
          const dd = d?.data_documento;
          if (!dd) return null;
          if (typeof dd === "string") {
            const parsed = new Date(dd);
            return isNaN(parsed) ? null : parsed;
          }
          if (typeof dd === "object" && typeof dd.toDate === "function") {
            try {
              return dd.toDate();
            } catch (_) {
              return null;
            }
          }
          const parsed = new Date(dd);
          return isNaN(parsed) ? null : parsed;
        })
        .filter(Boolean)
    : [];
  const baseCompetenzaDate =
    datesDoc.length > 0 ? datesDoc.sort((a, b) => a - b)[0] : new Date();

  // Periodo esplicito fornito dall'utente (mese/anno), con fallback
  // sulla data più datata dei documenti inclusi.
  const periodoMese = Number(consuntivoData?.periodo_mese);
  const periodoAnno = Number(consuntivoData?.periodo_anno);
  const hasPeriodoEsplicito =
    Number.isInteger(periodoMese) &&
    periodoMese >= 1 &&
    periodoMese <= 12 &&
    Number.isInteger(periodoAnno) &&
    periodoAnno > 1900;

  const competenzaLabel = hasPeriodoEsplicito
    ? `${monthsIT[periodoMese - 1]} ${periodoAnno}`
    : `${monthsIT[baseCompetenzaDate.getMonth()]} ${baseCompetenzaDate.getFullYear()}`;
  const clienteRef =
    (consuntivoData && consuntivoData.cliente) ||
    (Array.isArray(documents) && documents[0]?.cliente) ||
    "";
  const settoreCodice =
    (consuntivoData &&
      consuntivoData.settore &&
      (consuntivoData.settore.codice || consuntivoData.settore.code)) ||
    "";
  const safe = (s) =>
    String(s || "")
      .replace(/[\\/:*?"<>|]+/g, "_")
      .trim();
  const defaultFileNameBase = [
    safe(clienteRef),
    safe(settoreCodice),
    competenzaLabel,
  ]
    .filter(Boolean)
    .join(" - ");

  // Load Header Image once
  const headerImgData = await loadImageAsDataUrl(Intestazione);

  // Helper to add header to any jsPDF instance
  const addHeaderToDoc = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginRight = 15;
    const marginLeft = 15;

    if (headerImgData) {
      try {
        doc.addImage(headerImgData, "JPEG", marginLeft, 10, 50, 0);
      } catch (e) {
        console.error("Errore logo", e);
      }
    }
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text("Consuntivo Lavori", pageWidth / 2, 20, { align: "center" });
    doc.setFontSize(10);
    if (clienteRef) {
      doc.text(`${clienteRef}`, pageWidth - marginRight, 16, {
        align: "right",
      });
    }
    doc.text(`${competenzaLabel}`, pageWidth - marginRight, 22, {
      align: "right",
    });
  };

  // Prepare Interventions Data
  let grandTotal = 0;
  const summaryData = [];

  const interventions = selectedIds
    .map((id) => {
      const docData = documents.find((d) => d.id === id);
      if (!docData) return null;

      const docMaterials = consuntivoData.materiali.filter(
        (m) => m.docId === id,
      );
      const docLabor = consuntivoData.manodopera.filter((m) => m.docId === id);
      const docCallFees = (consuntivoData.dirittiChiamata || []).filter(
        (d) => d.docId === id && d?.applicato,
      );

      // Calculate totals
      const totMat = docMaterials.reduce(
        (acc, m) =>
          acc +
          parseNumberLoose(m.quantita) * parseNumberLoose(m.prezzo_unitario),
        0,
      );
      const totLab = docLabor.reduce(
        (acc, m) =>
          acc +
          parseNumberLoose(m.totale_ore) * parseNumberLoose(m.tariffa_oraria),
        0,
      );
      const totCallFee = docCallFees.reduce(
        (acc, fee) => acc + getCallFeeTotal(fee),
        0,
      );
      const totIntervention = totMat + totLab + totCallFee;

      return {
        doc: docData,
        materials: docMaterials,
        labor: docLabor,
        callFees: docCallFees,
        totMat,
        totLab,
        totCallFee,
        totIntervention,
      };
    })
    .filter((i) => i !== null);

  // Fill summaryData and grandTotal
  interventions.forEach((item) => {
    grandTotal += item.totIntervention;
    summaryData.push({
      rif: item.doc.numero_documento || "N/A",
      luogo: item.doc.luogo_intervento || "-",
      oggetto: item.doc.oggetto_intervento || "Intervento generico",
      materiali: item.totMat,
      manodopera: item.totLab,
      dirittoChiamata: item.totCallFee,
      totale: item.totIntervention,
    });
  });

  // Create the main PDF container
  const mergedPdf = await PDFDocument.create();

  // --- 1. GENERATE SUMMARY PAGE(S) ---
  const summaryDoc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  summaryDoc.setFont("helvetica", "normal");
  addHeaderToDoc(summaryDoc);

  let finalY = 40;
  const marginLeft = 15;
  const marginRight = 15;
  const pageWidth = summaryDoc.internal.pageSize.getWidth();

  summaryDoc.setFontSize(14);
  summaryDoc.setFont("helvetica", "bold");
  summaryDoc.setTextColor(161, 16, 0); // Rosso Giuvi
  summaryDoc.text("RIEPILOGO GENERALE", marginLeft, finalY);
  finalY += 8;

  autoTable(summaryDoc, {
    startY: finalY,
    head: [
      [
        "Rif. Documento",
        "Luogo Intervento",
        "Oggetto Intervento",
        "Materiali",
        "Manodopera",
        "Diritto",
        "Totale",
      ],
    ],
    body: summaryData.map((s) => [
      s.rif,
      s.luogo,
      s.oggetto,
      `€ ${s.materiali.toFixed(2)}`,
      `€ ${s.manodopera.toFixed(2)}`,
      `€ ${s.dirittoChiamata.toFixed(2)}`,
      `€ ${s.totale.toFixed(2)}`,
    ]),
    theme: "striped",
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [161, 16, 0], textColor: 255 },
    columnStyles: {
      0: { cellWidth: 26 },
      1: { cellWidth: 30 },
      2: { cellWidth: "auto" },
      3: { cellWidth: 22, halign: "right" },
      4: { cellWidth: 22, halign: "right" },
      5: { cellWidth: 18, halign: "right" },
      6: { cellWidth: 22, halign: "right" },
    },
    margin: { left: marginLeft, right: marginRight, top: 40 },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) addHeaderToDoc(summaryDoc);
    },
  });

  finalY = summaryDoc.lastAutoTable.finalY + 15;

  summaryDoc.setFontSize(16);
  summaryDoc.setFont("helvetica", "bold");
  summaryDoc.setTextColor(0, 0, 0);
  summaryDoc.text(
    `TOTALE COMPLESSIVO: € ${grandTotal.toFixed(2)}`,
    pageWidth - marginRight,
    finalY,
    { align: "right" },
  );

  // Add Summary to Merged PDF
  const summaryBytes = summaryDoc.output("arraybuffer");
  const summaryPdfLoaded = await PDFDocument.load(summaryBytes);
  const summaryPages = await mergedPdf.copyPages(
    summaryPdfLoaded,
    summaryPdfLoaded.getPageIndices(),
  );
  summaryPages.forEach((page) => mergedPdf.addPage(page));

  // --- 2. LOOP INTERVENTIONS: DETAIL + ATTACHMENT ---
  for (const intervention of interventions) {
    const {
      doc: docData,
      materials,
      labor,
      callFees,
      totCallFee,
      totIntervention,
    } = intervention;
    const aggregatedLabor = aggregateLabor(labor);

    // A. Create Detail PDF for this intervention
    const detailDoc = new jsPDF({ unit: "mm", format: "a4", compress: true });
    detailDoc.setFont("helvetica", "normal");
    addHeaderToDoc(detailDoc);

    let detailY = 40;
    const pageH = detailDoc.internal.pageSize.getHeight();
    const pageW = detailDoc.internal.pageSize.getWidth();

    // Intestazione Intervento
    detailDoc.setFontSize(12);
    detailDoc.setFont("helvetica", "bold");
    detailDoc.setFillColor(240, 240, 240);
    detailDoc.rect(
      marginLeft,
      detailY,
      pageW - (marginLeft + marginRight),
      8,
      "F",
    );
    detailDoc.setTextColor(0, 0, 0);
    detailDoc.text(
      `Rif: ${docData.numero_documento} - Data: ${formatSafeDate(
        docData.data_documento,
      )}`,
      marginLeft + 2,
      detailY + 5.5,
    );
    detailY += 12;

    // Oggetto
    detailDoc.setFontSize(10);
    detailDoc.setFont("helvetica", "normal");
    const splitObj = detailDoc.splitTextToSize(
      `Oggetto: ${docData.oggetto_intervento || "-"}`,
      pageW - (marginLeft + marginRight),
    );
    detailDoc.text(splitObj, marginLeft, detailY);
    detailY += splitObj.length * 5 + 2;

    // Luogo
    const splitLuogo = detailDoc.splitTextToSize(
      `Luogo: ${docData.luogo_intervento || "-"}`,
      pageW - (marginLeft + marginRight),
    );
    detailDoc.text(splitLuogo, marginLeft, detailY);
    detailY += splitLuogo.length * 5 + 2;

    // Tabella Materiali
    if (materials.length > 0) {
      autoTable(detailDoc, {
        startY: detailY,
        head: [["Descrizione", "Qtà", "Unità", "Prezzo Unit.", "Totale"]],
        body: materials.map((m) => [
          m.descrizione,
          m.quantita,
          m.unita_misura || "",
          `€ ${parseNumberLoose(m.prezzo_unitario).toFixed(2)}`,
          `€ ${(
            parseNumberLoose(m.quantita) * parseNumberLoose(m.prezzo_unitario)
          ).toFixed(2)}`,
        ]),
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 1.5 },
        headStyles: { fillColor: [100, 100, 100], textColor: 255 },
        margin: { left: marginLeft, right: marginRight, top: 40 },
        didDrawPage: (data) => {
          if (data.pageNumber > 1) addHeaderToDoc(detailDoc);
        },
      });
      detailY = detailDoc.lastAutoTable.finalY + 5;
    }

    // Tabella Manodopera
    if (aggregatedLabor.length > 0) {
      autoTable(detailDoc, {
        startY: detailY,
        head: [["Voce", "Ore", "Tariffa/h", "Totale"]],
        body: aggregatedLabor.map((m) => {
          const ore = parseNumberLoose(m.totale_ore);
          const tariffa = parseNumberLoose(m.tariffa_oraria);
          return [
            m.nome_tecnico,
            ore.toFixed(2),
            `€ ${tariffa.toFixed(2)}`,
            `€ ${(ore * tariffa).toFixed(2)}`,
          ];
        }),
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 1.5 },
        headStyles: { fillColor: [100, 100, 100], textColor: 255 },
        columnStyles: {
          0: { cellWidth: "auto" },
          1: { cellWidth: 24, halign: "right" },
          2: { cellWidth: 28, halign: "right" },
          3: { cellWidth: 28, halign: "right" },
        },
        margin: { left: marginLeft, right: marginRight, top: 40 },
        didDrawPage: (data) => {
          if (data.pageNumber > 1) addHeaderToDoc(detailDoc);
        },
      });
      detailY = detailDoc.lastAutoTable.finalY + 5;
    }

    // Diritto di Chiamata
    if (totCallFee > 0) {
      if (detailY > pageH - 20) {
        detailDoc.addPage();
        addHeaderToDoc(detailDoc);
        detailY = 40;
      }

      const boxX = marginLeft;
      const boxWidth = pageW - (marginLeft + marginRight);
      const rowHeight = 5;
      const details = callFees.length
        ? callFees.map((fee) => {
            const qta = parseNumberLoose(fee.qta ?? fee.quantita ?? 1);
            const prezzoUnitario = parseNumberLoose(
              fee.prezzo_unitario ??
                fee.prezzoUnitario ??
                fee.diritto_chiamata_prezzo_unitario ??
                fee.diritto_chiamata,
            );
            const totale = getCallFeeTotal(fee);
            return { qta, prezzoUnitario, totale };
          })
        : [{ qta: 1, prezzoUnitario: totCallFee, totale: totCallFee }];

      const boxHeight = 12 + details.length * rowHeight + 8;
      detailDoc.setFillColor(248, 248, 248);
      detailDoc.setDrawColor(161, 16, 0);
      detailDoc.roundedRect(boxX, detailY, boxWidth, boxHeight, 2, 2, "FD");

      detailDoc.setFont("helvetica", "bold");
      detailDoc.setFontSize(11);
      detailDoc.setTextColor(161, 16, 0);
      detailDoc.text("Diritto di Chiamata", boxX + 3, detailY + 6);

      detailDoc.setFont("helvetica", "normal");
      detailDoc.setFontSize(9);
      detailDoc.setTextColor(0, 0, 0);

      let rowY = detailY + 11;
      details.forEach((item) => {
        detailDoc.text(
          `Q.ta: ${item.qta.toFixed(2)}  |  Prezzo Unit.: EUR ${item.prezzoUnitario.toFixed(2)}  |  Totale: EUR ${item.totale.toFixed(2)}`,
          boxX + 3,
          rowY,
        );
        rowY += rowHeight;
      });

      detailDoc.setFont("helvetica", "bold");
      detailDoc.setFontSize(10);
      detailDoc.text(
        `Totale Diritto di Chiamata: EUR ${totCallFee.toFixed(2)}`,
        boxX + boxWidth - 3,
        detailY + boxHeight - 3,
        { align: "right" },
      );

      detailY += boxHeight + 6;
    }

    // Totale Intervento
    detailDoc.setFont("helvetica", "bold");
    detailDoc.text(
      `Totale Intervento: € ${totIntervention.toFixed(2)}`,
      pageW - marginRight,
      detailY,
      { align: "right" },
    );
    detailY += 15;

    // B. Handle Image Attachment (embedded in Detail PDF)
    if (docData.fotoUrl && !docData.fotoUrl.includes(".pdf")) {
      detailDoc.addPage();
      addHeaderToDoc(detailDoc);

      detailDoc.setFont("helvetica", "normal");
      detailDoc.setFontSize(10);
      detailDoc.text(
        `Allegato: ${docData.numero_documento || "Documento"}`,
        marginLeft,
        40,
      );

      try {
        const base64Img = await loadAttachmentAsDataUrl(docData.fotoUrl);
        if (base64Img) {
          const imgProps = detailDoc.getImageProperties(base64Img);
          const ratio = imgProps.width / imgProps.height;

          let w = pageW - (marginLeft + marginRight);
          let h = w / ratio;

          const maxH = pageH - 60;
          if (h > maxH) {
            h = maxH;
            w = h * ratio;
          }

          detailDoc.addImage(base64Img, "JPEG", marginLeft, 50, w, h);
        } else {
          detailDoc.text("Errore caricamento immagine.", marginLeft, 50);
        }
      } catch (err) {
        console.error("Errore allegato", err);
        detailDoc.text("Impossibile caricare allegato.", marginLeft, 50);
      }
    }

    // Add Detail PDF to Merged
    const detailBytes = detailDoc.output("arraybuffer");
    const detailPdfLoaded = await PDFDocument.load(detailBytes);
    const detailPages = await mergedPdf.copyPages(
      detailPdfLoaded,
      detailPdfLoaded.getPageIndices(),
    );
    detailPages.forEach((page) => mergedPdf.addPage(page));

    // C. Handle PDF Attachment (merged externally)
    if (docData.fotoUrl && docData.fotoUrl.includes(".pdf")) {
      // Check for insecure connection if needed, but assuming https or proxy
      if (String(docData.fotoUrl).startsWith("https://")) {
        try {
          const response = await fetch(docData.fotoUrl);
          if (!response.ok) throw new Error("Fetch failed");
          const attachmentBytes = await response.arrayBuffer();

          const attachmentPdf = await PDFDocument.load(attachmentBytes);
          const copiedPages = await mergedPdf.copyPages(
            attachmentPdf,
            attachmentPdf.getPageIndices(),
          );
          copiedPages.forEach((page) => mergedPdf.addPage(page));
        } catch (err) {
          console.error("Errore unione PDF allegato:", err);
          const page = mergedPdf.addPage();
          page.drawText(
            `Errore caricamento allegato PDF: ${docData.numero_documento}`,
            { x: 50, y: 700, size: 12 },
          );
        }
      } else {
        // Insecure PDF
        const page = mergedPdf.addPage();
        page.drawText(
          `Allegato PDF non incluso per connessione non sicura (HTTP): ${docData.numero_documento}`,
          { x: 50, y: 700, size: 10 },
        );
      }
    }
  }

  // Final Save
  try {
    const finalPdfBytes = await mergedPdf.save();
    const blob = new Blob([finalPdfBytes], { type: "application/pdf" });
    const fileName = `${
      defaultFileNameBase ||
      `Consuntivo_${new Date().toISOString().slice(0, 10)}`
    }.pdf`;

    if (typeof onReady === "function") {
      onReady(blob, fileName);
    } else {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (e) {
    console.error("Errore salvataggio PDF finale", e);
    alert("Errore generazione PDF.");
  }
}
